// ═════════════════════════════════════════════════════════════════════════════
// Scheme Service-Name admin API client
//
// Wraps the EMR scheme-alias endpoints (see ITEM6-SCHEME-ALIAS-DEEP-DIVE.md at the
// repo root). A "scheme" (WBHS, CGHS, …) is just a payer flagged is_scheme=true;
// each mapping aliases a hospital service_code → the name/code a scheme prints on
// its invoices. All calls are tenant-scoped EMR wrappers under /app/api/ipd/billing.
// The UI never talks to the invoice microservice directly and never sends
// X-Tenant-ID — EMR headers only (same conventions as corporate-api.js).
//
//   Scheme payers   GET/POST  /ipd/billing/corporate-payers/   (payer_type=GOVERNMENT, is_scheme)
//   Mappings        GET/POST  /ipd/billing/scheme-service-mappings/[{id}/]
//                   POST .../{id}/update/ | .../{id}/delete/  (edit / soft-deactivate)
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import { getBillingHeaders, trimId, sanitizeErrorMessage } from "./billing-api";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const PAYERS_ROOT = `${API_ROOT}ipd/billing/corporate-payers`;
const MAPPINGS_ROOT = `${API_ROOT}ipd/billing/scheme-service-mappings`;

/** The five scheme-type enum values the payer form / API accept. */
export const SCHEME_TYPES = [
  { id: "CENTRAL_GOVERNMENT", label: "Central Government" },
  { id: "STATE_GOVERNMENT", label: "State Government" },
  { id: "PSU", label: "PSU" },
  { id: "EMPLOYER", label: "Employer" },
  { id: "OTHER", label: "Other" },
];

// ── low-level helpers (same shape as corporate-api.js) ────────────────────────

function unwrapObject(data) {
  if (!data || typeof data !== "object") return data;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

function unwrapList(data, keys = []) {
  if (Array.isArray(data)) return data;
  for (const key of ["items", "data", "results", ...keys]) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object" && Array.isArray(val.items)) return val.items;
  }
  return [];
}

/** JSON request with the shared billing error handling. */
async function request(method, url, body) {
  try {
    const res = await axios({
      method,
      url,
      data: body,
      headers: getBillingHeaders(body != null),
    });
    if (res.data?.status === "Failed") {
      const wrapped = new Error(sanitizeErrorMessage(res.data.message || "Request failed"));
      wrapped.status = res.data.status_code || res.data.code;
      throw wrapped;
    }
    return res.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    if (err && err.status !== undefined && !err.response) throw err;
    const status = err.response?.status;
    const rawMsg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      (typeof err.response?.data === "string" ? err.response.data : "") ||
      err.message ||
      "Request failed";
    const wrapped = new Error(sanitizeErrorMessage(rawMsg, status));
    wrapped.status = status;
    wrapped.code = err.response?.data?.code || err.response?.data?.error_code;
    throw wrapped;
  }
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ── mappers (API → UI) ────────────────────────────────────────────────────────

export function mapSchemePayer(p = {}) {
  return {
    id: p.id ?? p.payer_id,
    code: p.payer_code ?? p.code ?? "—",
    name: p.payer_name ?? p.name ?? "—",
    type: p.payer_type ?? "GOVERNMENT",
    isScheme: p.is_scheme ?? false,
    schemeType: p.scheme_type ?? null,
    schemeAuthorityName: p.scheme_authority_name ?? null,
    active: p.is_active ?? p.active ?? true,
  };
}

export function mapSchemeMapping(m = {}) {
  return {
    id: m.id ?? m.mapping_id,
    payerId: m.payer_id ?? null,
    hospitalServiceCode: m.hospital_service_code ?? null,
    hospitalServiceName: m.hospital_service_name ?? null,
    schemeServiceCode: m.scheme_service_code ?? null,
    schemeServiceName: m.scheme_service_name ?? null,
    effectiveFrom: m.effective_from ?? null,
    effectiveTo: m.effective_to ?? null,
    priority: m.priority ?? 100,
    remarks: m.remarks ?? "",
    active: m.is_active ?? true,
  };
}

// ── Scheme payers ─────────────────────────────────────────────────────────────

/**
 * List scheme payers for the selector. Schemes are government payers flagged
 * is_scheme=true — we load payer_type=GOVERNMENT then keep only the schemes so a
 * non-scheme payer can never be picked (the create API would 400 on it).
 *   GET /corporate-payers/?payer_type=GOVERNMENT&active_only=true&search=&limit=&offset=
 */
export async function listSchemePayers({ search, limit = 50, offset = 0, activeOnly = true } = {}) {
  const data = await request(
    "GET",
    `${PAYERS_ROOT}/${qs({
      payer_type: "GOVERNMENT",
      active_only: activeOnly,
      search,
      limit,
      offset,
    })}`
  );
  return unwrapList(data, ["payers"]).map(mapSchemePayer).filter((p) => p.isScheme);
}

/**
 * Create a scheme payer.
 *   POST /corporate-payers/  {payer_code, payer_name, payer_type:"GOVERNMENT",
 *     is_scheme:true, scheme_type, scheme_authority_name}  → { payer_id, payer }
 */
export async function createSchemePayer(body) {
  const data = await request("POST", `${PAYERS_ROOT}/`, body);
  const obj = unwrapObject(data);
  const payer = mapSchemePayer(obj.payer || obj);
  if (!payer.id && obj.payer_id) payer.id = obj.payer_id;
  return payer;
}

/**
 * Edit an existing payer's scheme flags/fields (mark it a scheme, change
 * scheme_type / scheme_authority_name, rename). GET/POST only on the platform.
 *   POST /corporate-payers/{id}/update/  {is_scheme, scheme_type, scheme_authority_name, ...}
 */
export async function updateSchemePayer(payerId, body) {
  const data = await request("POST", `${PAYERS_ROOT}/${trimId(payerId)}/update/`, body);
  const obj = unwrapObject(data);
  const payer = mapSchemePayer(obj.payer || obj);
  if (!payer.id && obj.payer_id) payer.id = obj.payer_id;
  return payer;
}

// ── Scheme service-name mappings ──────────────────────────────────────────────

/**
 * List a scheme payer's mappings for the saved-lines table.
 *   GET /scheme-service-mappings/?payer_id=<uuid>&is_active=true
 *       [&hospital_service_code=&effective_on=YYYY-MM-DD]
 */
export async function listSchemeMappings({ payerId, isActive = true, hospitalServiceCode, effectiveOn } = {}) {
  const data = await request(
    "GET",
    `${MAPPINGS_ROOT}/${qs({
      payer_id: payerId,
      is_active: isActive,
      hospital_service_code: hospitalServiceCode,
      effective_on: effectiveOn,
    })}`
  );
  return unwrapList(data, ["mappings"]).map(mapSchemeMapping);
}

export async function getSchemeMapping(mappingId) {
  const data = await request("GET", `${MAPPINGS_ROOT}/${trimId(mappingId)}/`);
  const obj = unwrapObject(data);
  return mapSchemeMapping(obj.mapping || obj);
}

/** POST /scheme-service-mappings/ → 201 { mapping }. 400 payer-not-scheme, 409 overlap. */
export async function createSchemeMapping(body) {
  const data = await request("POST", `${MAPPINGS_ROOT}/`, body);
  const obj = unwrapObject(data);
  return mapSchemeMapping(obj.mapping || obj);
}

/** POST /scheme-service-mappings/{id}/update/ — partial; accepts any create field (GET/POST only). */
export async function updateSchemeMapping(mappingId, body) {
  const data = await request("POST", `${MAPPINGS_ROOT}/${trimId(mappingId)}/update/`, body);
  const obj = unwrapObject(data);
  return mapSchemeMapping(obj.mapping || obj);
}

/** POST /scheme-service-mappings/{id}/delete/ — soft delete (is_active=false) (GET/POST only). */
export async function deleteSchemeMapping(mappingId) {
  const data = await request("POST", `${MAPPINGS_ROOT}/${trimId(mappingId)}/delete/`);
  return unwrapObject(data);
}
